import { createElementSchemaId } from '@blaze-shared/schema-domain';
import {
  type Connection,
  MAIN_INPUT_NAME,
  type Workflow,
  type WorkflowInputs,
} from '@blaze-shared/workflow-domain';

import { extractVariablesFromString } from './references/parse-string';
import {
  collectColumnReferences,
  collectNamedInputReferences,
} from './references/workflow-node-references';
import { type WorkflowValidationContext } from './validation-context/workflow-validation-context';

/**
 * Whether the context holds enough to trust the result of validating this workflow.
 *
 * A node that reads an input whose schema has not been loaded, the page context, or a local
 * variable cannot be told apart from a node referencing something that no longer exists —
 * both look like a missing column. Callers wait rather than report those false positives.
 */
export function isWorkflowReadyForValidation(
  workflow: Workflow,
  context: WorkflowValidationContext,
): boolean {
  return getNodesNotReadyForValidation(workflow, context).length === 0;
}

function getNodesNotReadyForValidation(
  workflow: Workflow,
  context: WorkflowValidationContext,
): string[] {
  const nodesNotReadyForValidation: string[] = [];
  const notLoadedInputs = getNotLoadedInputSchemas(
    workflow.inputs,
    Object.keys(context.schemaRegistry.tableSchemas),
    context.connections || [],
  );
  workflow.nodes.forEach((node) => {
    const columns = collectColumnReferences([node]);
    const inputs = collectNamedInputReferences([node]);

    const hasReferenceToNotLoadedInput =
      columns.some((c) => c.inputName && notLoadedInputs.includes(c.inputName)) ||
      inputs.some((i) => i.namedInput && notLoadedInputs.includes(i.namedInput));

    if (hasReferenceToNotLoadedInput) {
      nodesNotReadyForValidation.push(node.id);
      return;
    }

    const haveReferenceToPageContext = columns.some((c) => c.inputName === 'Page');
    if (haveReferenceToPageContext) {
      nodesNotReadyForValidation.push(node.id);
      return;
    }

    const variables = extractVariablesFromString(JSON.stringify(node));
    const haveReferenceToLocalVariables = variables.length;
    if (haveReferenceToLocalVariables) {
      nodesNotReadyForValidation.push(node.id);
    }
  });

  return nodesNotReadyForValidation;
}

/**
 * Names of the workflow's named inputs whose source element/node has no schema among the
 * provided ones — i.e. inputs whose schema has not been loaded (or fetched) yet.
 *
 * Exported for its own tests; not part of the library's public API.
 */
export function getNotLoadedInputSchemas(
  wfInputs: WorkflowInputs,
  providedSchemaIds: string[],
  connections: Connection[],
): string[] {
  const notLoadedInputs: string[] = [];

  Object.keys(wfInputs).forEach((inputName) => {
    const connectionId = wfInputs[inputName];
    if (inputName === MAIN_INPUT_NAME) {
      return;
    }

    const connection = connections.find(({ id }) => id === connectionId);
    if (!connection?.source?.id) {
      return;
    }

    const schemaKey =
      connection.source.layerType === 'Ui'
        ? createElementSchemaId(connection.source.id, connection.eventType)
        : connection.source.id;
    if (!schemaKey) {
      return;
    }

    if (!providedSchemaIds.includes(schemaKey)) {
      notLoadedInputs.push(inputName);
    }
  });

  return notLoadedInputs;
}
